import {
  TRY_DENOMINATIONS,
  type DayCloseSnapshot,
  type DenominationCountEntry,
  type NonCashTenderCount,
  type TryDenominationCode,
  type VarianceRow,
  type VarianceTolerancePolicy,
} from "./types";

/** Format minor units (kuruş) → "1.234,56" (TR) or "1,234.56" (EN) */
export function formatMinor(minor: number, locale: "tr" | "en" = "tr"): string {
  const major = minor / 100;
  return major.toLocaleString(locale === "tr" ? "tr-TR" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatMoney(minor: number, currencyPrefix = "₺", locale: "tr" | "en" = "tr"): string {
  const sign = minor < 0 ? "-" : "";
  return `${sign}${currencyPrefix}${formatMinor(Math.abs(minor), locale)}`;
}

export function formatSignedMoney(
  minor: number,
  currencyPrefix = "₺",
  locale: "tr" | "en" = "tr",
): string {
  if (minor === 0) return `±${currencyPrefix}${formatMinor(0, locale)}`;
  const sign = minor > 0 ? "+" : "−";
  return `${sign}${currencyPrefix}${formatMinor(Math.abs(minor), locale)}`;
}

export function formatBusinessDate(iso: string, locale: "tr" | "en" = "tr"): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale === "tr" ? "tr-TR" : "en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatTime(iso: string, locale: "tr" | "en" = "tr"): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(locale === "tr" ? "tr-TR" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Sum denomination counts → total minor units */
export function denominationsTotal(
  counts: readonly DenominationCountEntry[],
): number {
  let total = 0;
  for (const entry of counts) {
    const denom = TRY_DENOMINATIONS.find((d) => d.code === entry.code);
    if (denom) total += denom.unitMinor * entry.count;
  }
  return total;
}

export function denominationByCode(code: TryDenominationCode) {
  return TRY_DENOMINATIONS.find((d) => d.code === code)!;
}

export function getCount(
  counts: readonly DenominationCountEntry[],
  code: TryDenominationCode,
): number {
  return counts.find((c) => c.code === code)?.count ?? 0;
}

export function setCount(
  counts: readonly DenominationCountEntry[],
  code: TryDenominationCode,
  count: number,
): DenominationCountEntry[] {
  const safe = Math.max(0, Math.min(500, Math.floor(count)));
  const exists = counts.some((c) => c.code === code);
  if (exists) {
    return counts.map((c) => (c.code === code ? { ...c, count: safe } : c));
  }
  return [...counts, { code, count: safe }];
}

/** Compute variance row */
export function computeVarianceRow(
  method: VarianceRow["method"],
  expectedMinor: number,
  countedMinor: number,
  tolerance: VarianceTolerancePolicy,
): VarianceRow {
  const variance = countedMinor - expectedMinor;
  const limit = method === "cash" ? tolerance.cashMinor : tolerance.perTenderMinor;
  return {
    method,
    expectedMinor,
    countedMinor,
    varianceMinor: variance,
    withinTolerance: Math.abs(variance) <= limit,
  };
}

export function recomputeVariance(snapshot: DayCloseSnapshot): DayCloseSnapshot {
  const cashCounted = denominationsTotal(snapshot.cash.denominationCounts);
  const cashRow = computeVarianceRow(
    "cash",
    snapshot.cash.expectedCashMinor,
    cashCounted,
    snapshot.tolerance,
  );
  const tenderRows: VarianceRow[] = snapshot.nonCashTenders.map((t) =>
    computeVarianceRow(t.method, t.expectedMinor, t.countedMinor, snapshot.tolerance),
  );
  const rows = [cashRow, ...tenderRows];
  const totalAbsMinor = rows.reduce((acc, r) => acc + Math.abs(r.varianceMinor), 0);
  const withinTolerance = rows.every((r) => r.withinTolerance);
  const requiresOwnerOverride = rows.some(
    (r) => Math.abs(r.varianceMinor) >= snapshot.tolerance.hardBlockMinor,
  );
  const requiresManagerOverride = !withinTolerance && !requiresOwnerOverride;
  return {
    ...snapshot,
    cash: { ...snapshot.cash, countedCashMinor: cashCounted },
    variance: {
      rows,
      totalAbsMinor,
      withinTolerance,
      requiresManagerOverride,
      requiresOwnerOverride,
    },
  };
}

export function expectedTotalMinor(snapshot: DayCloseSnapshot): number {
  const nonCash = snapshot.nonCashTenders.reduce((a, t) => a + t.expectedMinor, 0);
  return snapshot.cash.expectedCashMinor + nonCash;
}

export function countedTotalMinor(snapshot: DayCloseSnapshot): number {
  const cash = denominationsTotal(snapshot.cash.denominationCounts);
  const nonCash = snapshot.nonCashTenders.reduce((a, t) => a + t.countedMinor, 0);
  return cash + nonCash;
}

export const STEP_ORDER = [
  "prerequisites", "cash", "non_cash", "review", "submit",
] as const;

export function nextStep(s: typeof STEP_ORDER[number]) {
  const i = STEP_ORDER.indexOf(s);
  return i >= 0 && i < STEP_ORDER.length - 1 ? STEP_ORDER[i + 1] : s;
}
export function prevStep(s: typeof STEP_ORDER[number]) {
  const i = STEP_ORDER.indexOf(s);
  return i > 0 ? STEP_ORDER[i - 1] : s;
}

/** Has any blocked prerequisites */
export function hasBlockedPrereqs(snapshot: DayCloseSnapshot): boolean {
  return snapshot.prerequisites.some((p) => p.status === "blocked");
}

/** Whether deposit (closing float + deposit) sums to counted cash */
export function depositBalanced(snapshot: DayCloseSnapshot): boolean {
  const counted = denominationsTotal(snapshot.cash.denominationCounts);
  return snapshot.deposit.closingFloatMinor + snapshot.deposit.depositAmountMinor === counted;
}

export function tenderUpdate(
  list: readonly NonCashTenderCount[],
  method: NonCashTenderCount["method"],
  patch: Partial<NonCashTenderCount>,
): NonCashTenderCount[] {
  return list.map((t) => (t.method === method ? { ...t, ...patch } : t));
}
